/* istanbul ignore file */

import React, { useEffect } from 'react';
import { batch, connect, ConnectedProps } from 'react-redux';
import { getCookieConsent } from '@noths/polaris-client-gdpr-compliance';
import { DeliveryZoneCode, parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import * as recommendations from '@noths/polaris-client-recommendations';
import {
  extraMaxWidthContainer,
  grid,
  maxWidthContainer,
} from '@noths/polaris-client-ribbons-base';
import { visuallyHidden } from '@noths/polaris-client-styles';
import {
  actions as userConfigurationActions,
  CookieConsent,
  getOrSetLoop54UserId,
  getOrSetRichRelevanceSessionId,
  getUserAttributes,
  selectFeatureFlags,
} from '@noths/polaris-client-user-configuration';
import { addRUMTiming } from '@noths/polaris-client-utils';
import { StatusCode } from '@noths/polaris-dev-ts-types';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { parse } from 'url';

import { ApiErrorGuardContainer } from 'src/components/molecules/ApiErrorGuard';
import { BreadcrumbContainer } from 'src/components/molecules/Breadcrumb';
import { PreventSwipeNavigation } from 'src/components/molecules/PreventSwipeNavigation';
import { StickySection } from 'src/components/molecules/StickySection';
import { DeliveryAreaContainer } from 'src/components/organisms/DeliveryArea';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel';
import { OverallRatingContainer } from 'src/components/organisms/OverallRating';
import { PartnerStorefrontContainer } from 'src/components/organisms/PartnerStorefront';
import { ProductDescriptionContainer } from 'src/components/organisms/ProductDescription';
import { ProductImagesCarouselContainer } from 'src/components/organisms/ProductImagesCarousel';
import {
  AddToBasketButtonContainer,
  MAIN_PAGE_CTA_ID,
  PRODUCT_REVIEWS_ID,
  ProductInfoContainer,
} from 'src/components/organisms/ProductInfo';
import { ProductReviewsContainer } from 'src/components/organisms/ProductReviews';
import { RecommendedProductsShelfContainer } from 'src/components/organisms/RecommendedProductsShelf';
import { ERROR_PRODUCT_NOT_FOUND } from 'src/constants/errors';
import { SimilarProductsShelf } from 'src/constants/featureFlags';
import { Placement } from 'src/constants/recommendations';
import { getBasketWithOptions } from 'src/redux/basket';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import {
  actions as deliveryZoneOptionsActions,
  requestDeliveryZoneOptions,
} from 'src/redux/deliveryZoneOptions';
import { trackLoop54Event } from 'src/redux/loop54';
import {
  actions as personalisationActions,
  updatePersonalisationOptionsFromBasketItem,
} from 'src/redux/personalisation';
import { requestProductDetails } from 'src/redux/product';
import { requestProductReviews } from 'src/redux/reviews';
import { actions as richRelevanceActions } from 'src/redux/richRelevance';
import { recommendedProductsRequestComplete } from 'src/redux/richRelevance/actions';
import { requestRecommendedProducts } from 'src/redux/richRelevance/thunks';
import { selectProductJSONLDSchema } from 'src/redux/selectors';
import { AppThunkDispatch, getStore } from 'src/redux/store';
import { fetchGlobalContent } from 'src/services/contentstack';
import * as styles from 'src/styles/productDetails.styles';
import { NextConfig, PageProps } from 'src/types';
import { getDisplayPageLoadPlacements, isOutOfStock, isSuspended } from 'src/utils';
import { getFeatureFlagsWithMatchers } from 'src/utils/getFeatureFlagsWithMatchers';
import { getIsRequestFromExternalReferrer } from 'src/utils/page';

type ProductDetailsPageProps = PageProps & ConnectedProps<typeof connector>;

export const CONTENT_SECTION_ID = 'product-details__content';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

const mapStateToProps = (state: ReduxApplicationState) => ({
  available: !isSuspended(state.product.availability, state.product.available),
  isRequestFromExternalReferrer: state.richRelevance.isRequestFromExternalReferrer,
  isSimilarProductsShelfVariant:
    selectFeatureFlags(state)?.[SimilarProductsShelf.NAME] === SimilarProductsShelf.VARIANT ??
    false,
  inventoryStatus: state.product.inventoryStatus,
  metaDescription: state.product.metaDescription,
  partnerName: state.product.partner.name,
  productJSONLDSchema: selectProductJSONLDSchema(state),
  productTitle: state.product.title,
  productCode: state.product.code,
  categoryHintId: state.product.breadcrumbCategoryId,
  richRelevanceSessionId: state.userConfiguration.richRelevanceSessionId,
  outOfStockPlacementHasData: (state.richRelevance[Placement.OutOfStock].products ?? []).length > 0,
  activeProductsPlacementHasData:
    (state.richRelevance[Placement.ActiveProducts].products ?? []).length > 0,
  recentlyViewedPlacementHasData:
    (state.richRelevance[Placement.RecentlyViewed].products ?? []).length > 0,
  similarProductsPlacementHasData:
    (state.richRelevance[Placement.SimilarProducts].products ?? []).length > 0,
  pageLiveRegionText: state.accessibility.statusMessage,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
  getBasket: () => dispatch(getBasketWithOptions()),
  updateEditedItem: (editedItem: string) =>
    dispatch(personalisationActions.updateEditedItem(editedItem)),
  updatePersonalisationOptionsFromBasketItem: () =>
    dispatch(updatePersonalisationOptionsFromBasketItem()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

const ProductDetailsPage = ({
  available,
  canonicalUrl,
  categoryHintId,
  activeProductsPlacementHasData,
  outOfStockPlacementHasData,
  recentlyViewedPlacementHasData,
  similarProductsPlacementHasData,
  inventoryStatus,
  isRequestFromExternalReferrer,
  isSimilarProductsShelfVariant,
  metaDescription,
  partnerName,
  productCode,
  productJSONLDSchema,
  productTitle,
  richRelevanceSessionId,
  getBasket,
  updateEditedItem,
  updatePersonalisationOptionsFromBasketItem,
  pageLiveRegionText,
}: ProductDetailsPageProps) => {
  const { dispatch } = getStore();
  const { query } = useRouter();

  const showSimilarProducts = isSimilarProductsShelfVariant || isRequestFromExternalReferrer;

  useEffect(() => {
    getUserAttributes()
      .then((attributes) =>
        recommendations.initRecommendations({
          apiKey: publicRuntimeConfig.RICH_RELEVANCE_API_KEY,
          env: process.env.NODE_ENV,
          sessionId: richRelevanceSessionId,
          userId: attributes?.sub,
        }),
      )
      .then(async () => {
        recommendations.setProductDetailPage(productCode, productTitle);
        recommendations.setProductContextCategory(categoryHintId!);

        const placements = isOutOfStock(inventoryStatus)
          ? [Placement.OutOfStock, Placement.SameSeller]
          : [
              ...(showSimilarProducts ? [Placement.SimilarProducts] : []),
              Placement.RecentlyViewed,
              Placement.ActiveProducts,
              Placement.SameSeller,
            ];

        const placementData = await recommendations.getRecommendations(placements);

        const recommendationsRequests = placementData.map((placement) =>
          dispatch(requestRecommendedProducts(placement)),
        );

        await Promise.allSettled(recommendationsRequests);
        addRUMTiming('recommendationsLoaded');
      })
      .catch((e) => {
        console.warn(`Could not load recommendations on product details page: ${e.message}`);
      })
      .finally(() => {
        dispatch(recommendedProductsRequestComplete());
      });
  });

  useEffect(() => {
    if (query.editItem) {
      retrieveEditItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.editItem]);

  useEffect(() => {
    if (productCode) {
      (dispatch as AppThunkDispatch)(requestDeliveryZoneOptions());
      (dispatch as AppThunkDispatch)(requestProductReviews({}));
    }
    (dispatch as AppThunkDispatch)(trackLoop54Event({ eventType: 'click' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrieveEditItem = async () => {
    updateEditedItem(query.editItem as string);
    await getBasket();
    updatePersonalisationOptionsFromBasketItem();
  };

  const {
    displaySimilarProductsPlacement,
    displayRecentlyViewedPlacement,
    displayActiveProductsPlacement,
    displayOutOfStockPlacement,
  } = getDisplayPageLoadPlacements({
    inventoryStatus,
    available,
    showSimilarProducts,
    similarProductsPlacementHasData,
    recentlyViewedPlacementHasData,
    activeProductsPlacementHasData,
    outOfStockPlacementHasData,
  });

  return (
    <>
      <Head>
        <title>{`${productTitle} By ${partnerName}`} | notonthehighstreet.com</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(productJSONLDSchema) }}
        />
      </Head>
      <ApiErrorGuardContainer>
        <div css={visuallyHidden} role="status">
          {pageLiveRegionText}
        </div>
        {/* Note: the id and data attribute are used by the VM team to open PDP pages in the CMS */}
        <div css={styles.wrapper}>
          <div id="product" data-product-id={productCode}>
            <div css={[grid, maxWidthContainer]}>
              <div css={styles.breadcrumbContainer}>
                <BreadcrumbContainer />
              </div>
            </div>
            <PreventSwipeNavigation data-hide-placement={!displaySimilarProductsPlacement}>
              {displaySimilarProductsPlacement && (
                <div css={styles.similarProductInnerContainer}>
                  <div css={[grid, extraMaxWidthContainer]}>
                    <div css={[styles.similarProductShelf]}>
                      <RecommendedProductsShelfContainer
                        placementType={Placement.SimilarProducts}
                        hideProductTitles
                        titleAlignment="left"
                      />
                    </div>
                  </div>
                </div>
              )}
            </PreventSwipeNavigation>

            <PreventSwipeNavigation
              css={[grid, extraMaxWidthContainer]}
              data-hide-placement={!displayOutOfStockPlacement}
            >
              {displayOutOfStockPlacement && (
                <div css={styles.recommendationCarousel}>
                  <RecommendedProductsShelfContainer placementType={Placement.OutOfStock} />
                </div>
              )}
            </PreventSwipeNavigation>
            <div
              id={CONTENT_SECTION_ID}
              css={[grid, maxWidthContainer, styles.imagesAndInfoWrapper]}
            >
              <div css={styles.imagesContainer}>
                <ProductImagesCarouselContainer />
              </div>
              <div css={styles.productInfoContainer}>
                <ProductInfoContainer />
              </div>
            </div>
            <PreventSwipeNavigation css={[maxWidthContainer, styles.productDescriptionContainer]}>
              <ProductDescriptionContainer />
            </PreventSwipeNavigation>
            <div
              css={[grid, maxWidthContainer, styles.productReviewsOuterWrapper]}
              id={PRODUCT_REVIEWS_ID}
            >
              <div css={styles.productReviewsWrapper}>
                <ProductReviewsContainer />
              </div>
              <div css={styles.overallRatingWrapper}>
                <OverallRatingContainer />
              </div>
            </div>
          </div>
          <div css={[grid, maxWidthContainer]}>
            <DeliveryAreaContainer />
          </div>
          <PreventSwipeNavigation
            css={[
              grid,
              extraMaxWidthContainer,
              styles.recommendedProductsContainer,
              styles.activeProductsContainer,
            ]}
            data-hide-placement={!displayActiveProductsPlacement}
          >
            {displayActiveProductsPlacement && (
              <div css={styles.recommendationCarousel}>
                <RecommendedProductsShelfContainer
                  lazyImages
                  placementType={Placement.ActiveProducts}
                />
              </div>
            )}
          </PreventSwipeNavigation>
          <div css={styles.storefrontWrapper}>
            <PartnerStorefrontContainer />
          </div>
          <div css={[grid, maxWidthContainer]}>
            <div css={styles.linkCarouselWrapper}>
              <LinkCarouselContainer />
            </div>
          </div>
          <PreventSwipeNavigation
            css={[grid, extraMaxWidthContainer, styles.recommendedProductsContainer]}
            data-hide-placement={!displayRecentlyViewedPlacement}
          >
            {displayRecentlyViewedPlacement && (
              <div css={styles.recommendationCarousel}>
                <RecommendedProductsShelfContainer
                  lazyImages
                  placementType={Placement.RecentlyViewed}
                />
              </div>
            )}
          </PreventSwipeNavigation>
          <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
            <AddToBasketButtonContainer />
          </StickySection>
        </div>
      </ApiErrorGuardContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query, res, req }) => {
  const pathname = parse(req.url!).pathname!;
  const domain = publicRuntimeConfig.hosts.domain;
  const canonicalUrl = `https://${domain}${pathname}`;
  const isRequestFromExternalReferrer = getIsRequestFromExternalReferrer(req, query);

  const { dispatch, getState } = getStore({
    router: { pathname, query },
  });

  const {
    countryCode: initialCountryCode,
    currency: initialCurrency,
    deliveryZone: initialDeliveryZone,
  } = parseLocaleSetCookieHeaders(res.getHeaders());

  const { country_code, currency, delivery_zone, rr_session, Loop54User } = req.cookies;
  const richRelevanceSessionId = getOrSetRichRelevanceSessionId(res, rr_session);
  const cookieConsent: CookieConsent = getCookieConsent(req.cookies);
  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};

  const countryCode = initialCountryCode ?? country_code;
  const currencyCode = initialCurrency ?? currency;
  const deliveryZone = (initialDeliveryZone ?? delivery_zone) as DeliveryZoneCode;

  batch(() => {
    dispatch(userConfigurationActions.setCountryCode(countryCode));
    dispatch(userConfigurationActions.setCookieConsent(cookieConsent));
    dispatch(userConfigurationActions.setCurrencyCode(currencyCode));
    dispatch(userConfigurationActions.setDeliveryZone(deliveryZone));
    dispatch(userConfigurationActions.setRichRelevanceSessionId(richRelevanceSessionId));
    dispatch(userConfigurationActions.setFeatureFlags(featureFlags));
    dispatch(richRelevanceActions.setIsRequestFromExternalReferrer(isRequestFromExternalReferrer));
    dispatch(deliveryZoneOptionsActions.setSelectedCountry(countryCode));
    dispatch(deliveryZoneOptionsActions.setSelectedZone(deliveryZone));

    if (cookieConsent.functionalCookiesConsented) {
      const loop54UserId = getOrSetLoop54UserId(res, Loop54User);
      dispatch(userConfigurationActions.setLoop54UserId(loop54UserId));
    }
  });

  const [, [navData, topBannerData]] = await Promise.all([
    (dispatch as AppThunkDispatch)(
      requestProductDetails({
        cookie: req.headers.cookie,
        query: {
          uri: pathname,
          previewing: query.previewing,
        },
        isHealthcheckRequest: req.headers['health-check'] === 'true',
      }),
    ),
    fetchGlobalContent({ featureFlags }),
  ]);

  const state: ReduxApplicationState = getState();
  const {
    deliveryZoneOptions,
    personalisation,
    product,
    productImagesCarousel,
    reviews,
    richRelevance,
    userConfiguration,
  } = state;

  if (product.redirectUrl) {
    return {
      redirect: {
        destination: product.redirectUrl,
        statusCode: StatusCode.MovedPermanently,
      },
    };
  }

  if (product.error) {
    res.statusCode =
      product.error.name === ERROR_PRODUCT_NOT_FOUND
        ? StatusCode.NotFound
        : StatusCode.InternalServerError;

    if (res.statusCode === StatusCode.InternalServerError) {
      res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
    }
  }

  const props: PageProps = {
    canonicalUrl,
    navData,
    topBannerData,
    updatedReduxState: {
      // Cherry pick the state to be updated on the client
      deliveryZoneOptions,
      personalisation,
      product,
      productImagesCarousel,
      reviews,
      richRelevance,
      userConfiguration,
    },
  };

  return { props };
};

export default connector(ProductDetailsPage);
